import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { isProduction } from '@/features/shared/utils/environment'
import { logError } from '@/features/shared/utils/logger'
import styled from 'styled-components'
import { ClientOnly } from '@/features/shared/components/ClientOnly'
import { isOnServer } from '@shared/constants/util'
import { Body } from '@shipt/design-system-typography'
import { Box, Row } from '@shipt/design-system-layouts'
import { WarningTriangleIcon } from '@shipt/design-system-icons'
import { theme } from '@/features/shared/constants/shiptTheme'

type CMSErrorHandlerProps = {
  error: unknown
  id: string
  contentTypeId: string
}

export const CMSErrorHandler = ({ error, id, contentTypeId }: CMSErrorHandlerProps) => {
  if (isOnServer()) {
    logError(error, { id, contentTypeId })
  }

  // Do not render out an error message in production
  if (isProduction) return null

  // Render out an error message in development & staging
  return (
    <GridLayoutContainer {...getCMSWrapperProps({ content_type_id: contentTypeId, id })}>
      <ErrorBlock>
        <Row spacing="xs">
          <WarningTriangleIcon concept="feedback" feedbackType="negative" variant="secondary" />
          <Body concept="feedback" feedbackType="negative" variant="primary">
            Error rendering CMS content type:&nbsp;
            <ContentTypeId as="pre" strong>
              {contentTypeId}
            </ContentTypeId>
          </Body>
        </Row>
        {error instanceof Error && (
          // render the error stack trace only on the client side to avoid SSR issues
          <ClientOnly>
            <ErrorMessage>{error.stack}</ErrorMessage>
          </ClientOnly>
        )}
      </ErrorBlock>
    </GridLayoutContainer>
  )
}

const ErrorBlock = styled(Box)`
  border: 1px dashed ${theme.color.border.feedback.negative.loud};
  border-radius: 4px;
  background-color: ${theme.color.background.base.neutral.subtle_100};
  color: ${theme.color.content.feedback.negative.primary};
  padding: 16px;
`

const ErrorMessage = styled.pre`
  overflow: auto;
`

const ContentTypeId = styled(Body)`
  display: inline;
`
